// customer details page - Admin View (Readonly)
import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Chip,
  Button,
  Avatar,
  LinearProgress,
  IconButton,
  Alert,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Badge,
  Divider,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  TrendingUp as TrendingUpIcon,
  People as PeopleIcon,
  AttachMoney as MoneyIcon,
  Assessment as AnalyticsIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Business as BusinessIcon,
  LocationOn as LocationIcon,
  Schedule as ScheduleIcon,
  Payment as PaymentIcon,
  Receipt as ReceiptIcon,
  Star as StarIcon,
  Warning as WarningIcon,
  Send as SendIcon,
  CalendarToday as CalendarIcon,
  Info as InfoIcon,
  CheckCircle as CheckIcon,
  Cancel as CancelIcon,
  VerifiedUser as VerifiedUserIcon,
  Upgrade as UpgradeIcon,
  Group as GroupIcon,
  History as HistoryIcon,
  Description as DescriptionIcon,
  Pending as PendingIcon,
  Call as CallIcon,
  QueryBuilder as QueryIcon,
  Assignment as AssignmentIcon,
  AccountCircle as AccountIcon,
  Timer as TimerIcon,
  Notifications as NotificationsIcon,
  Help as HelpIcon,
  Close as CloseIcon,
  Download as DownloadIcon,
  TrendingDown as TrendingDownIcon,
  Speed as SpeedIcon,
  Visibility as VisibilityIcon,
  Work as WorkIcon,
  School as SchoolIcon,
  LocalShipping as ShippingIcon,
  Store as StoreIcon,
  Apartment as ApartmentIcon,
  Home as HomeIcon,
  BusinessCenter as BusinessCenterIcon,
  Domain as DomainIcon,
  CorporateFare as CorporateFareIcon,
  QuestionAnswer as QuestionAnswerIcon,
  Add as AddIcon,
  Assessment as AssessmentIcon
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import {
  formatIndianCurrency,
  formatDate,
  formatRelativeTime,
  getStatusColor,
  getPlanTypeColor,
  getInitials,
  getQueryStatusColor,
  fetchQueryStatusFilters,
  fetchInvoiceStatusFilters
} from '../../../utils/helper';
import api from '../../../services/api';

// Import billing components
import InvoiceList from './InvoiceList';
import CustomerSnapshot from './CustomerSnapshot';
import CreateInvoice from '../../../components/common/CreateInvoice';
import QueryStatus from '../../../components/common/QueryStatus';
import QueryStatusFilter from '../../../components/common/QueryStatusFilter';
import InvoiceActions from '../../../components/common/InvoiceActions';
import InvoiceDetails from '../../../components/common/InvoiceDetails';
import InvoiceStatusUpdateDialog from '../../../components/common/InvoiceStatusUpdateDialog';
import SendInvoiceDialog from '../../../components/common/SendInvoiceDialog';
import { useInvoices } from '../../../context/InvoiceContext';

// API functions for customer data
const fetchCustomerAccountDetails = async (customerId) => {
  try {
    const response = await api.get(`customers/${customerId}/account_detail/`);
    return response.data.data;
  } catch (error) {
    console.error('Error fetching customer account details:', error);
    throw error;
  }
};

const fetchCustomerQueries = async (customerId) => {
  try {
    const response = await api.get(`customer-query/${customerId}/`);
    return {
      queries: response.data.data,
      pendingQueries: response.data.pendingQueries
    };
  } catch (error) {
    console.error('Error fetching customer queries:', error);
    throw error;
  }
};


const CustomerDetails = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  
  // Use InvoiceContext for invoice actions
  const { 
    updateInvoiceStatus: contextUpdateInvoiceStatus, 
    shareInvoice: contextShareInvoice,
    addPayment: contextAddPayment,
    getInvoiceDetails: contextGetInvoiceDetails,
    fetchAccountInvoices: contextFetchAccountInvoices
  } = useInvoices();
  const [customer, setCustomer] = useState(null);
  const [queries, setQueries] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [createInvoiceModal, setCreateInvoiceModal] = useState(false);
  const [selectedQueryStatus, setSelectedQueryStatus] = useState('all');
  const [selectedInvoiceStatus, setSelectedInvoiceStatus] = useState('all');
  const [queryStatusOptions, setQueryStatusOptions] = useState([]);
  const [invoiceStatusOptions, setInvoiceStatusOptions] = useState([]);
  const [loadingFilters, setLoadingFilters] = useState(false);

  // Use context methods for invoice actions
  const updateInvoiceStatus = contextUpdateInvoiceStatus;
  const shareInvoice = contextShareInvoice;

  // Invoice action states
  const [selectedInvoiceForAction, setSelectedInvoiceForAction] = useState(null);
  const [invoiceDetailsOpen, setInvoiceDetailsOpen] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [openPaymentOnMount, setOpenPaymentOnMount] = useState(false);
  
  // Invoice status update dialog state
  const [statusUpdateDialog, setStatusUpdateDialog] = useState({
    open: false,
    action: null, // 'mark_paid' or 'cancel'
    loading: false
  });
  
  // Send invoice dialog state
  const [sendInvoiceDialog, setSendInvoiceDialog] = useState({
    open: false,
    loading: false
  });

  // Reset openPaymentOnMount when invoice details dialog closes
  useEffect(() => {
    if (!invoiceDetailsOpen) {
      setOpenPaymentOnMount(false);
    }
  }, [invoiceDetailsOpen]);



  // Fetch filter options
  const fetchFilterOptions = async () => {
    try {
      setLoadingFilters(true);
      
      // Fetch query status filter options using helper function
      const queryStatusResponse = await fetchQueryStatusFilters();
      setQueryStatusOptions(queryStatusResponse.data);

      // Fetch invoice status filter options using helper function
      const invoiceStatusOptions = await fetchInvoiceStatusFilters();
      setInvoiceStatusOptions(invoiceStatusOptions);
    } catch (error) {
      console.error('Error fetching filter options:', error);
      setSnackbar({
        open: true,
        message: 'Failed to load filter options',
        severity: 'error'
      });
    } finally {
      setLoadingFilters(false);
    }
  };

  useEffect(() => {
    const fetchCustomerData = async () => {
      if (!id) return;
      
      setLoading(true);
      setError(null);
      
      try {
        // First fetch account details to get account_id
        const accountDetails = await fetchCustomerAccountDetails(id);
        
        // Then fetch other data in parallel using account_id
        console.log('Fetching invoices for account_id:', accountDetails.account_info.id);
        const [queriesData, invoicesData] = await Promise.all([
          fetchCustomerQueries(id),
          contextFetchAccountInvoices(id)
        ]);
        
        console.log('Invoices API response:', invoicesData);

        // Use only backend data
        const combinedCustomerData = {
          ...accountDetails,
          stats: {
            users: accountDetails.users || {
              totalTeams: 0,
              totalUsers: 0
            },
            engagement: accountDetails.engagement || {
              activeUsers7Days: 0,
              activeUsers30Days: 0
            },
            requests: accountDetails.requests || {
              raised: 0,
              pending: 0,
              processed: 0,
              rejected: 0
            },
            calls: accountDetails.call_details || {
              totalCalls: 0,
              totalDuration: 0,
              todayCalls: 0
            }
          },
          pendingRequests: accountDetails.pendingRequests || [],
          timeline: accountDetails.timeline || [],
          queries: queriesData.queries || [],
          invoices: invoicesData.success ? invoicesData.data || [] : [],
          additionalStats: {
            pendingQueries: queriesData.pendingQueries || 0,
            invoiceDue: invoicesData.success ? (invoicesData.data || []).reduce((sum, inv) => sum + (inv.due_amount || 0), 0) : 0,
            pendingInvoice: invoicesData.success ? (invoicesData.data || []).filter(inv => inv.status === 'UP').length : 0,
            totalReceived: invoicesData.success ? (invoicesData.data || []).reduce((sum, inv) => sum + (inv.total_paid_amount || 0), 0) : 0,
            totalInvoices: invoicesData.success ? (invoicesData.data || []).length : 0
          }
        };

        console.log("Customer data:", combinedCustomerData);
        console.log("Queries data:", queriesData);
        console.log("Invoices data:", invoicesData);
        
        if (!invoicesData.success) {
          console.error('Failed to fetch invoices:', invoicesData.message);
          setSnackbar({
            open: true,
            message: `Failed to load invoices: ${invoicesData.message}`,
            severity: 'error'
          });
        }
        
        setCustomer(combinedCustomerData);
        setQueries(queriesData.queries);
        setInvoices(invoicesData.success ? invoicesData.data || [] : []);
      } catch (error) {
        console.error('Error fetching customer data:', error);
        setError('Failed to load customer data. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchCustomerData();
    // Fetch filter options
    fetchFilterOptions();
  }, [id]);

  const handleSendEmail = () => {
    setSnackbar({
      open: true,
      message: 'Email sent successfully',
      severity: 'success'
    });
  };

  const handleUpgradePlan = () => {
    setSnackbar({
      open: true,
      message: 'Plan upgrade initiated',
      severity: 'success'
    });
  };


  const handleSaveInvoice = async (invoiceData) => {
    // Simulate API call to save invoice
    console.log('Saving invoice:', invoiceData);
    
    // Add the new invoice to the customer's invoices
    const newInvoice = {
      id: Date.now(),
      invoice_number: `INV-${String(Date.now()).slice(-6)}`,
      ...invoiceData,
      status: 'UP',
      status_verbose: 'Unpaid',
      created_at: new Date().toISOString().split('T')[0],
      updated_at: new Date().toISOString().split('T')[0],
      payment_history: []
    };

    setCustomer(prev => ({
      ...prev,
      invoices: [newInvoice, ...prev.invoices]
    }));

    setSnackbar({
      open: true,
      message: 'Invoice created successfully',
      severity: 'success'
    });
  };

  // Invoice action handlers - simplified to work with InvoiceActions component
  const handleViewInvoice = async (invoice) => {
    try {
      // Get detailed invoice data from context
      const response = await contextGetInvoiceDetails(invoice.id);
      if (response.success && response.data) {
        setSelectedInvoice(response.data);
      } else {
        console.warn('Failed to get detailed invoice data, using basic data:', response);
        setSelectedInvoice(invoice); // Fallback to basic data
      }
    } catch (error) {
      console.error('Error fetching invoice details:', error);
      setSelectedInvoice(invoice); // Fallback to basic data
    }
    setInvoiceDetailsOpen(true);
  };

  const handleSendInvoice = async (invoiceId) => {
    setSendInvoiceDialog(prev => ({ ...prev, loading: true }));
    try {
      const response = await shareInvoice(invoiceId);
      
      if (response.success) {
        setSnackbar({
          open: true,
          message: response.message || 'Invoice sent successfully',
          severity: 'success'
        });
        
        // Close the dialog
        setSendInvoiceDialog({ open: false, loading: false });
      } else {
        throw new Error(response.message || 'Failed to send invoice');
      }
    } catch (error) {
      console.error('Error sending invoice:', error);
      
      let errorMessage = 'Failed to send invoice';
      
      if (error.message) {
        errorMessage = error.message;
      }

      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error'
      });
      
      setSendInvoiceDialog(prev => ({ ...prev, loading: false }));
    }
  };

  const handleActionDownload = async (invoice) => {
    try {
      console.log('Selected invoice for download:', invoice);
      console.log('Invoice URL field:', invoice.invoice_url);
      
      // Check multiple possible URL fields
      const invoiceUrl = invoice.invoice_url || 
                        invoice.invoiceUrl || 
                        invoice.pdf_url ||
                        invoice.url;
      
      if (invoiceUrl) {
        console.log('Using invoice URL:', invoiceUrl);
        
        // Method 1: Try direct download with fetch for better error handling
        try {
          const response = await fetch(invoiceUrl);
          if (response.ok) {
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `${invoice.invoice_number}.pdf`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
            
            setSnackbar({
              open: true,
              message: `Invoice ${invoice.invoice_number} downloaded successfully`,
              severity: 'success'
            });
          } else {
            throw new Error(`HTTP ${response.status}: ${response.statusText}`);
          }
        } catch (fetchError) {
          console.log('Fetch method failed, trying direct link method:', fetchError);
          
          // Method 2: Fallback to direct link method
          const link = document.createElement('a');
          link.href = invoiceUrl;
          link.download = `${invoice.invoice_number}.pdf`;
          link.target = '_blank';
          link.rel = 'noopener noreferrer';
          
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          
          setSnackbar({
            open: true,
            message: `Invoice ${invoice.invoice_number} download initiated`,
            severity: 'success'
          });
        }
      } else {
        console.log('No invoice URL found. Available fields:', Object.keys(invoice));
        setSnackbar({
          open: true,
          message: 'Invoice PDF URL is not available. Please contact support.',
          severity: 'error'
        });
      }
    } catch (error) {
      console.error('Error downloading invoice:', error);
      setSnackbar({
        open: true,
        message: 'Failed to download invoice: ' + error.message,
        severity: 'error'
      });
    }
  };

  // Status update dialog handlers
  const handleStatusUpdateClose = () => {
    setStatusUpdateDialog({
      open: false,
      action: null,
      loading: false
    });
    // Clear the selected invoice when dialog is closed
    setSelectedInvoiceForAction(null);
  };

  const handleStatusUpdateConfirm = async (statusData) => {
    if (!selectedInvoiceForAction) return;

    setStatusUpdateDialog(prev => ({ ...prev, loading: true }));

    try {
      const response = await updateInvoiceStatus(selectedInvoiceForAction.id, statusData);
      
      if (response.success) {
        // Show success message
        setSnackbar({
          open: true,
          message: response.message || 'Invoice status updated successfully',
          severity: 'success'
        });

        // Close the dialog
        handleStatusUpdateClose();
        
        // Refresh customer data to update invoice list
        const accountDetails = await fetchCustomerAccountDetails(id);
        const invoicesData = await contextFetchAccountInvoices(accountDetails.account_info.id);
        setInvoices(invoicesData.success ? invoicesData.data || [] : []);
      } else {
        throw new Error(response.message || 'Failed to update invoice status');
      }
    } catch (error) {
      console.error('Error updating invoice status:', error);
      
      let errorMessage = 'Failed to update invoice status';
      
      if (error.message) {
        errorMessage = error.message;
      }

      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error'
      });
      
      setStatusUpdateDialog(prev => ({ ...prev, loading: false }));
    }
  };

  // Send invoice dialog handlers
  const handleSendInvoiceDialogClose = () => {
    setSendInvoiceDialog({ open: false, loading: false });
    setSelectedInvoiceForAction(null);
  };

  const handleSendInvoiceDialogConfirm = async (invoiceId) => {
    await handleSendInvoice(invoiceId);
  };

  const getTimelineIcon = (type) => {
    const icons = {
      onboarding: <PeopleIcon />,
      verification: <VerifiedUserIcon />,
      subscription: <UpgradeIcon />,
      user_request: <GroupIcon />,
      payment: <MoneyIcon />,
      call: <CallIcon />,
      invoice: <ReceiptIcon />
    };
    return icons[type] || <HistoryIcon />;
  };

  const getTimelineColor = (type, status) => {
    if (status === 'expired') return 'error';
    if (status === 'pending') return 'warning';
    
    const colors = {
      onboarding: 'primary',
      verification: 'success',
      subscription: 'info',
      user_request: 'secondary',
      payment: 'success',
      call: 'info',
      invoice: 'warning'
    };
    return colors[type] || 'default';
  };

  const getRequestTypeIcon = (type) => {
    const icons = {
      upgrade: <UpgradeIcon />,
      add_user: <PeopleIcon />,
      renew: <UpgradeIcon />,
      add_credits: <MoneyIcon />
    };
    return icons[type] || <PendingIcon />;
  };

  const getInvoiceStatusColor = (status) => {
    const colors = {
      PD: 'success',
      UP: 'warning',
      PP: 'info',
      CN: 'error',
      OV: 'error'
    };
    return colors[status] || 'default';
  };

  const getSubscriptionStatusColor = (customer) => {
    // For now, return a default color since we don't have status in the response
    // You may need to add status to the backend response
    return 'success';
  };

  const getSubscriptionStatusText = (customer) => {
    // For now, return a default text since we don't have status in the response
    // You may need to add status to the backend response
    return 'Active';
  };

  const calculateRemainingDays = (subscriptionExpiry) => {
    // Handle the new format where subscriptionExpiry is an object
    if (subscriptionExpiry && typeof subscriptionExpiry === 'object') {
      return subscriptionExpiry.remainingDays || 0;
    }
    
    // Fallback for old format (date string)
    if (subscriptionExpiry) {
      const today = new Date();
      const expiry = new Date(subscriptionExpiry);
      const diffTime = expiry - today;
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      return diffDays;
    }
    
    return 0;
  };

  const formatDuration = (minutes) => {
    if (minutes < 1) return `${Math.round(minutes * 60)} Sec`;
    if (minutes < 60) return `${Math.round(minutes)} Min`;
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = Math.round(minutes % 60);
    if (hours < 24) {
      return remainingMinutes > 0 ? `${hours} Hr ${remainingMinutes} Min` : `${hours} Hr`;
    }
    const days = Math.floor(hours / 24);
    const remainingHours = hours % 24;
    return remainingHours > 0 ? `${days} Day ${remainingHours} Hr` : `${days} Day`;
  };

  const getBusinessCategoryIcon = (category) => {
    const icons = {
      'Real Estate': <ApartmentIcon />,
      'Technology': <BusinessCenterIcon />,
      'Healthcare': <WorkIcon />,
      'Education': <SchoolIcon />,
      'Retail': <StoreIcon />,
      'Manufacturing': <DomainIcon />,
      'Finance': <CorporateFareIcon />,
      'Transportation': <ShippingIcon />,
      'Consulting': <BusinessIcon />,
      'Marketing': <BusinessIcon />,
      'E-commerce': <StoreIcon />,
      'Food & Beverage': <StoreIcon />,
      'Automotive': <ShippingIcon />,
      'Pharmaceuticals': <WorkIcon />,
      'Textiles': <DomainIcon />,
      'Construction': <ApartmentIcon />,
      'Banking': <CorporateFareIcon />,
      'Insurance': <CorporateFareIcon />
    };
    return icons[category] || <BusinessIcon />;
  };

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <LinearProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (!customer) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">Customer not found</Alert>
      </Box>
    );
  }

  const renderInvoicesContent = () => {
    return (
      <InvoiceList 
        invoices={invoices}
        invoiceStatusOptions={invoiceStatusOptions}
        showSuccessMessage={(message, severity = 'success') => setSnackbar({
          open: true,
          message,
          severity
        })}
        onViewInvoice={handleViewInvoice}
        onSendInvoice={(invoice) => {
          setSelectedInvoiceForAction(invoice);
          setSendInvoiceDialog({ open: true, loading: false });
        }}
        onDownloadInvoice={handleActionDownload}
        onAddPayment={(invoice) => {
          setSelectedInvoice(invoice);
          setOpenPaymentOnMount(true);
          setInvoiceDetailsOpen(true);
        }}
        onCancelInvoice={(invoice) => {
          setSelectedInvoiceForAction(invoice);
          setStatusUpdateDialog({ open: true, action: 'cancel', loading: false });
        }}
        onMarkPaidInvoice={(invoice) => {
          setSelectedInvoiceForAction(invoice);
          setStatusUpdateDialog({ open: true, action: 'mark_paid', loading: false });
        }}
      />
    );
  };

  const renderQueriesContent = () => {
    // Filter queries based on selected status
    const filteredQueries = selectedQueryStatus === 'all' 
      ? queries 
      : queries.filter(query => {
          // Check both status and status_verbose for filtering
          return query.status === selectedQueryStatus || 
                 query.status_verbose === selectedQueryStatus ||
                 (queryStatusOptions.find(option => option.value === selectedQueryStatus)?.label === query.status_verbose);
        });



    return (
      <Box>
        {/* Filter Section */}
        <Box sx={{ mb: 3 }}>
          <QueryStatusFilter
            value={selectedQueryStatus}
            onChange={setSelectedQueryStatus}
            size="small"
            fullWidth
            label="Filter by Status"
            options={queryStatusOptions}
          />
        </Box>

        {/* Queries List */}
        {filteredQueries.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="body1" color="text.secondary">
              {queries.length === 0 ? 'No queries found for this customer.' : 'No queries found for the selected status.'}
            </Typography>
          </Box>
        ) : (
          filteredQueries.map((query, index) => (
            <Box key={query.id} sx={{ mb: 3 }}>
              {/* Status and Date Row */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <QueryStatus
                  status={query.status}
                  status_verbose={query.status_verbose}
                  size="small"
                  showIcon={true}
                />
                <Typography variant="subtitle2" color="text.primary" sx={{ fontWeight: 400 }}>
                  {query.created_at}
                </Typography>
              </Box>

              {/* Content */}
              <Typography variant="body1" sx={{ 
                whiteSpace: 'pre-line',
                lineHeight: 1.5,
                color: 'text.secondary',
                fontSize: '0.875rem'
              }}>
                {query.content}
              </Typography>

              {/* Divider between items */}
              {index < filteredQueries.length - 1 && <Divider sx={{ mt: 2, mb: 2 }} />}
            </Box>
          ))
        )}
      </Box>
    );
  };

  return (
    <Box sx={{ 
      p: { xs: 2, sm: 3, md: 4 }, 
      bgcolor: '#f7f9fc', 
      minHeight: '100vh',
      backgroundImage: 'linear-gradient(180deg, rgba(240,242,247,0.8) 0%, rgba(247,249,252,1) 100%)',
      position: 'relative',
      '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        height: '200px',
        background: 'linear-gradient(180deg, rgba(58,65,111,0.03) 0%, rgba(58,65,111,0) 100%)',
        zIndex: 0
      }
    }}>
      {/* Header Section */}
      <Card sx={{ 
        mb: 4, 
        borderRadius: 3, 
        boxShadow: '0 8px 32px rgba(0,0,0,0.12)', 
        background: 'linear-gradient(135deg, #3a416f 0%, #141727 100%)', 
        color: 'white',
        overflow: 'hidden',
        position: 'relative'
      }}>
        {/* Background pattern overlay */}
        <Box sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          opacity: 0.05,
          backgroundImage: 'radial-gradient(circle at 25px 25px, rgba(255,255,255,0.2) 2%, transparent 0%), radial-gradient(circle at 75px 75px, rgba(255,255,255,0.2) 2%, transparent 0%)',
          backgroundSize: '100px 100px',
          zIndex: 0
        }} />
        <CardContent sx={{ p: 4, position: 'relative', zIndex: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
            <Box sx={{ flex: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                <IconButton 
                  onClick={() => navigate('/customers')} 
                  sx={{ 
                    bgcolor: 'rgba(255,255,255,0.1)', 
                    color: 'white', 
                    backdropFilter: 'blur(10px)',
                    '&:hover': { 
                      bgcolor: 'rgba(255,255,255,0.2)',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
                    },
                    transition: 'all 0.2s ease'
                  }}
                >
                  <ArrowBackIcon />
                </IconButton>
                <Typography 
                  variant="h3" 
                  component="h1" 
                  fontWeight="800" 
                  sx={{ 
                    color: 'white', 
                    textShadow: '0 2px 4px rgba(0,0,0,0.3)',
                    letterSpacing: '-0.5px'
                  }}
                >
                  {customer.account_info?.company_name || 'Company Name Not Available'}
                </Typography>
              </Box>
              
              {/* Company Details Row */}
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 4, 
                mb: 3,
                flexWrap: { xs: 'wrap', md: 'nowrap' },
                '& > div': { 
                  minWidth: { xs: '100%', sm: 'auto' },
                  mb: { xs: 1, sm: 0 }
                }
              }}>
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 1.5,
                  px: 2,
                  py: 1,
                  bgcolor: 'rgba(255,255,255,0.1)',
                  borderRadius: 2,
                  backdropFilter: 'blur(10px)'
                }}>
                  <Avatar sx={{ 
                    bgcolor: 'rgba(255,255,255,0.2)', 
                    width: 32, 
                    height: 32 
                  }}>
                    <AccountIcon fontSize="small" />
                  </Avatar>
                  <Box>
                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)', display: 'block', fontSize: '0.7rem', fontWeight: 500 }}>
                      CONTACT PERSON
                    </Typography>
                    <Typography variant="body2" fontWeight="medium" sx={{ color: 'white' }}>
                      {customer.owner_contact?.name || 'Name Not Available'}
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 1.5,
                  px: 2,
                  py: 1,
                  bgcolor: 'rgba(255,255,255,0.1)',
                  borderRadius: 2,
                  backdropFilter: 'blur(10px)'
                }}>
                  <Avatar sx={{ 
                    bgcolor: 'rgba(255,255,255,0.2)', 
                    width: 32, 
                    height: 32 
                  }}>
                    <EmailIcon fontSize="small" />
                  </Avatar>
                  <Box>
                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)', display: 'block', fontSize: '0.7rem', fontWeight: 500 }}>
                      EMAIL
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'white' }}>
                      {customer.owner_contact?.email || 'Email Not Available'}
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 1.5,
                  px: 2,
                  py: 1,
                  bgcolor: 'rgba(255,255,255,0.1)',
                  borderRadius: 2,
                  backdropFilter: 'blur(10px)'
                }}>
                  <Avatar sx={{ 
                    bgcolor: 'rgba(255,255,255,0.2)', 
                    width: 32, 
                    height: 32 
                  }}>
                    <PhoneIcon fontSize="small" />
                  </Avatar>
                  <Box>
                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)', display: 'block', fontSize: '0.7rem', fontWeight: 500 }}>
                      PHONE
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'white' }}>
                      {customer.owner_contact?.mobile || 'Mobile Not Available'}
                    </Typography>
                  </Box>
                </Box>
              </Box>
              
              {/* Address */}
              <Box sx={{ 
                mb: 2,
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                px: 2,
                py: 1,
                bgcolor: 'rgba(255,255,255,0.1)',
                borderRadius: 2,
                maxWidth: 'fit-content',
                backdropFilter: 'blur(10px)'
              }}>
                <Avatar sx={{ 
                  bgcolor: 'rgba(255,255,255,0.2)', 
                  width: 32, 
                  height: 32 
                }}>
                  <LocationIcon fontSize="small" />
                </Avatar>
                <Box>
                  <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)', display: 'block', fontSize: '0.7rem', fontWeight: 500 }}>
                    ADDRESS
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'white', lineHeight: 1.6 }}>
                    {customer.account_info?.address || 'Address not provided'}, {customer.account_info?.state || 'State not provided'} - {customer.account_info?.pincode || 'Pincode not provided'}
                  </Typography>
                </Box>
              </Box>

              {/* Business Info */}
              <Box sx={{ 
                display: 'grid', 
                gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, 
                gap: 3, 
                mb: 4 
              }}>
                {/* GSTIN */}
                <Card sx={{ 
                  p: 2.5, 
                  borderRadius: 2, 
                  bgcolor: 'rgba(255,255,255,0.08)', 
                  backdropFilter: 'blur(10px)',
                  color: 'white', 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 2,
                  transition: 'all 0.3s ease',
                  border: '1px solid rgba(255,255,255,0.1)',
                  '&:hover': {
                    boxShadow: '0 8px 16px rgba(0,0,0,0.15)',
                    bgcolor: 'rgba(255,255,255,0.12)',
                    transform: 'translateY(-2px)'
                  }
                }}>
                  <Avatar sx={{ 
                    bgcolor: 'rgba(255,255,255,0.15)',
                    width: 48,
                    height: 48,
                    boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
                  }}>
                    <BusinessIcon />
                  </Avatar>
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography variant="caption" sx={{ 
                      opacity: 0.7, 
                      textTransform: 'uppercase', 
                      letterSpacing: '0.5px',
                      fontWeight: 600,
                      fontSize: '0.7rem'
                    }}>GSTIN</Typography>
                    <Typography variant="body1" fontWeight={600} sx={{ 
                      wordBreak: 'break-all', 
                      fontSize: '0.95rem',
                      color: 'white'
                    }}>
                      {customer.account_info?.gstin || 'GSTIN not provided'}
                    </Typography>
                  </Box>
                </Card>

                {/* Business Category */}
                <Card sx={{ 
                  p: 2.5, 
                  borderRadius: 2, 
                  bgcolor: 'rgba(255,255,255,0.08)', 
                  backdropFilter: 'blur(10px)',
                  color: 'white', 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 2,
                  transition: 'all 0.3s ease',
                  border: '1px solid rgba(255,255,255,0.1)',
                  '&:hover': {
                    boxShadow: '0 8px 16px rgba(0,0,0,0.15)',
                    bgcolor: 'rgba(255,255,255,0.12)',
                    transform: 'translateY(-2px)'
                  }
                }}>
                  <Avatar sx={{ 
                    bgcolor: 'rgba(255,255,255,0.15)',
                    width: 48,
                    height: 48,
                    boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
                  }}>
                    {getBusinessCategoryIcon(customer.account_info?.industry)}
                  </Avatar>
                  <Box>
                    <Typography variant="caption" sx={{ 
                      opacity: 0.7, 
                      textTransform: 'uppercase', 
                      letterSpacing: '0.5px',
                      fontWeight: 600,
                      fontSize: '0.7rem'
                    }}>Category</Typography>
                    <Typography variant="body1" fontWeight={600} sx={{ 
                      fontSize: '0.95rem',
                      color: 'white'
                    }}>{customer.account_info?.industry || 'Industry not specified'}</Typography>
                  </Box>
                </Card>
              </Box>

              {/* Additional Business Info */}
              <Box sx={{ 
                display: 'grid', 
                gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' }, 
                gap: 3, 
                mb: 3 
              }}>
                {/* Users */}
                <Card sx={{ 
                  p: 2.5, 
                  borderRadius: 2, 
                  bgcolor: 'rgba(255,255,255,0.08)', 
                  backdropFilter: 'blur(10px)',
                  color: 'white', 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 2,
                  transition: 'all 0.3s ease',
                  border: '1px solid rgba(255,255,255,0.1)',
                  '&:hover': {
                    boxShadow: '0 8px 16px rgba(0,0,0,0.15)',
                    bgcolor: 'rgba(255,255,255,0.12)',
                    transform: 'translateY(-2px)'
                  }
                }}>
                  <Avatar sx={{ 
                    bgcolor: 'rgba(79, 195, 247, 0.3)',
                    width: 48,
                    height: 48,
                    boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
                  }}>
                    <PeopleIcon sx={{ color: '#4fc3f7' }} />
                  </Avatar>
                  <Box>
                    <Typography variant="caption" sx={{ 
                      opacity: 0.7, 
                      textTransform: 'uppercase', 
                      letterSpacing: '0.5px',
                      fontWeight: 600,
                      fontSize: '0.7rem'
                    }}>Users</Typography>
                    <Typography variant="h6" fontWeight={700} sx={{ 
                      fontSize: '1.1rem',
                      color: 'white'
                    }}>
                      {customer.stats?.users?.totalUsers || 0} <Typography component="span" variant="body2" sx={{ opacity: 0.7, fontWeight: 400 }}>Active</Typography>
                    </Typography>
                  </Box>
                </Card>

                {/* Total Calls */}
                <Card sx={{ 
                  p: 2.5, 
                  borderRadius: 2, 
                  bgcolor: 'rgba(255,255,255,0.08)', 
                  backdropFilter: 'blur(10px)',
                  color: 'white', 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 2,
                  transition: 'all 0.3s ease',
                  border: '1px solid rgba(255,255,255,0.1)',
                  '&:hover': {
                    boxShadow: '0 8px 16px rgba(0,0,0,0.15)',
                    bgcolor: 'rgba(255,255,255,0.12)',
                    transform: 'translateY(-2px)'
                  }
                }}>
                  <Avatar sx={{ 
                    bgcolor: 'rgba(255, 183, 77, 0.3)',
                    width: 48,
                    height: 48,
                    boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
                  }}>
                    <TimerIcon sx={{ color: '#ffb74d' }} />
                  </Avatar>
                  <Box>
                    <Typography variant="caption" sx={{ 
                      opacity: 0.7, 
                      textTransform: 'uppercase', 
                      letterSpacing: '0.5px',
                      fontWeight: 600,
                      fontSize: '0.7rem'
                    }}>Total Call</Typography>
                    <Typography variant="h6" fontWeight={700} sx={{ 
                      fontSize: '1.1rem',
                      color: 'white'
                    }}>
                      {formatDuration((customer.stats?.calls?.totalDuration || 0) * 60)}
                    </Typography>
                  </Box>
                </Card>

                {/* Today's Calls */}
                <Card sx={{ 
                  p: 2.5, 
                  borderRadius: 2, 
                  background: 'linear-gradient(135deg, rgba(255,255,255,0.15) 0%, rgba(255,255,255,0.05) 100%)', 
                  backdropFilter: 'blur(10px)',
                  color: 'white', 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 2,
                  border: '1px solid rgba(255,255,255,0.2)',
                  boxShadow: '0 8px 16px rgba(0,0,0,0.15)',
                  position: 'relative',
                  overflow: 'hidden',
                  zIndex: 1,
                  '&:before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: 'linear-gradient(45deg, rgba(76, 175, 80, 0.15), rgba(76, 175, 80, 0))',
                    zIndex: -1
                  }
                }}>
                  <Avatar sx={{ 
                    bgcolor: 'rgba(76, 175, 80, 0.3)',
                    width: 48,
                    height: 48,
                    boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
                  }}>
                    <CallIcon sx={{ color: '#4caf50' }} />
                  </Avatar>
                  <Box>
                    <Typography variant="caption" sx={{ 
                      opacity: 0.9, 
                      fontWeight: 600,
                      textTransform: 'uppercase', 
                      letterSpacing: '0.5px',
                      fontSize: '0.7rem'
                    }}>Today's Call</Typography>
                    <Typography variant="h6" fontWeight={700} sx={{ fontSize: '1.2rem' }}>
                      {customer.stats?.calls?.todayCalls || 0} <Typography component="span" variant="body2" sx={{ opacity: 0.8, fontWeight: 400 }}>calls</Typography>
                    </Typography>
                  </Box>
                </Card>
              </Box>
            </Box>
            
            {/* Subscription Info Card - Right Side */}
            <Box sx={{ flex: '0 0 450px', ml: 3 }}>
              <Card
                sx={{
                  p: 0,
                  borderRadius: 3,
                  background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)',
                  color: 'white',
                  boxShadow: '0 10px 30px rgba(0,0,0,0.2)',
                  overflow: 'hidden',
                  position: 'relative',
                  border: '1px solid rgba(255,255,255,0.1)'
                }}
              >
                {/* Background pattern */}
                <Box sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  opacity: 0.05,
                  backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
                  zIndex: 0
                }} />
                
                {/* Header with accent color */}
                <Box sx={{
                  p: 2.5,
                  pb: 2,
                  background: 'linear-gradient(90deg, rgba(0,0,0,0.2) 0%, rgba(0,0,0,0) 100%)',
                  borderBottom: '1px solid rgba(255,255,255,0.1)',
                  position: 'relative',
                  zIndex: 1
                }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Avatar sx={{ 
                        bgcolor: 'rgba(255,255,255,0.15)',
                        width: 42,
                        height: 42,
                        boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
                      }}>
                        <VerifiedUserIcon sx={{ color: '#fff' }} />
                      </Avatar>
                      <Box>
                        <Typography variant="caption" sx={{ 
                          opacity: 0.7, 
                          textTransform: 'uppercase', 
                          letterSpacing: '0.5px',
                          fontWeight: 600,
                          fontSize: '0.7rem'
                        }}>SUBSCRIPTION</Typography>
                        <Typography variant="h6" fontWeight="800" sx={{ 
                          fontSize: '1.25rem',
                          background: 'linear-gradient(90deg, #ffffff, #e0e0e0)',
                          WebkitBackgroundClip: 'text',
                          WebkitTextFillColor: 'transparent',
                          textShadow: '0 2px 4px rgba(0,0,0,0.1)'
                        }}>
                          {customer.subscription_detail?.plan_name || 'No Plan'}
                        </Typography>
                      </Box>
                    </Box>
                    <Chip
                      label={getSubscriptionStatusText(customer)}
                      sx={{ 
                        bgcolor: getSubscriptionStatusColor(customer) === 'success' ? 'rgba(76, 175, 80, 0.9)' : 
                               getSubscriptionStatusColor(customer) === 'warning' ? 'rgba(255, 152, 0, 0.9)' : 'rgba(244, 67, 54, 0.9)',
                        color: 'white',
                        fontWeight: 'bold',
                        textShadow: '0 1px 2px rgba(0,0,0,0.3)',
                        border: '1px solid rgba(255,255,255,0.3)',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                      }}
                      size="small"
                    />
                  </Box>
                </Box>

                {/* Content */}
                <Box sx={{ p: 3, position: 'relative', zIndex: 1 }}>
                  {/* Expiry Visualization */}
                  <Box sx={{ 
                    mb: 3,
                    p: 2.5,
                    borderRadius: 2,
                    bgcolor: 'rgba(255,255,255,0.08)',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(255,255,255,0.1)',
                  }}>
                    {(() => {
                      const remainingDays = 0; // Since we don't have remaining days in the response
                      const isExpired = false; // Since we don't have expiry status in the response
                      
                      return (
                        <>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <CalendarIcon sx={{ fontSize: '1.2rem', color: 'rgba(255,255,255,0.7)' }} />
                              <Typography variant="body2" sx={{ 
                                opacity: 0.8, 
                                fontWeight: 500,
                                textTransform: 'uppercase',
                                letterSpacing: '0.5px',
                                fontSize: '0.75rem'
                              }}>
                                {isExpired ? 'EXPIRED ON' : 'EXPIRES ON'}
                              </Typography>
                            </Box>
                            <Typography variant="h6" fontWeight="bold" sx={{ 
                              fontSize: '1rem',
                              color: isExpired ? '#ff5252' : '#4fc3f7'
                            }}>
                              {customer.subscription_detail?.expiry || 'No expiry date'}
                            </Typography>
                          </Box>
                          {!isExpired && (
                            <Box>
                              <LinearProgress
                                variant="determinate"
                                value={Math.max(0, Math.min(100, (remainingDays / 30) * 100))}
                                sx={{ 
                                  height: 8, 
                                  borderRadius: 4, 
                                  bgcolor: 'rgba(255,255,255,0.1)',
                                  mb: 1,
                                  '& .MuiLinearProgress-bar': {
                                    bgcolor: 'linear-gradient(90deg, #4fc3f7, #2196f3)',
                                    borderRadius: 4
                                  }
                                }}
                              />
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)', fontWeight: 500 }}>
                                  {remainingDays} days left
                                </Typography>
                                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)', fontWeight: 500 }}>
                                  {Math.round(remainingDays / 30 * 100)}%
                                </Typography>
                              </Box>
                            </Box>
                          )}
                          {isExpired && (
                            <Box sx={{ 
                              display: 'flex', 
                              alignItems: 'center', 
                              gap: 1, 
                              bgcolor: 'rgba(244, 67, 54, 0.1)', 
                              p: 1, 
                              borderRadius: 1,
                              border: '1px solid rgba(244, 67, 54, 0.2)'
                            }}>
                              <WarningIcon sx={{ color: '#ff5252', fontSize: '1.2rem' }} />
                              <Typography variant="caption" sx={{ color: '#ff5252', fontWeight: 500 }}>
                                Subscription has expired
                              </Typography>
                            </Box>
                          )}
                        </>
                      );
                    })()}
                  </Box>

                  {/* Current Usage */}
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle2" fontWeight="bold" sx={{ 
                      mb: 2,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1
                    }}>
                      <SpeedIcon sx={{ fontSize: '1.2rem' }} />
                      Current Usage
                    </Typography>
                    
                    {/* Users Quota */}
                    <Box sx={{ 
                      mb: 2.5, 
                      p: 2,
                      borderRadius: 2,
                      bgcolor: 'rgba(255,255,255,0.05)',
                      border: '1px solid rgba(255,255,255,0.05)'
                    }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <PeopleIcon sx={{ fontSize: '1rem', color: 'rgba(255,255,255,0.7)' }} />
                          <Typography variant="body2" sx={{ opacity: 0.8, fontWeight: 500 }}>Users</Typography>
                        </Box>
                        <Typography variant="body2" fontWeight="bold" sx={{ color: '#4fc3f7' }}>
                          {customer.subscription_detail?.total_user || 0}/{customer.subscription_detail?.maxUsersAllowed || 0}
                        </Typography>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={customer.subscription_detail?.maxUsersAllowed ? ((customer.subscription_detail.total_user || 0) / customer.subscription_detail.maxUsersAllowed) * 100 : 0}
                        sx={{ 
                          height: 6, 
                          borderRadius: 3, 
                          bgcolor: 'rgba(255,255,255,0.1)',
                          '& .MuiLinearProgress-bar': {
                            background: 'linear-gradient(90deg, #4fc3f7, #2196f3)',
                            borderRadius: 3
                          }
                        }}
                      />
                    </Box>

                    {/* Call Quota */}
                    <Box sx={{ 
                      p: 2,
                      borderRadius: 2,
                      bgcolor: 'rgba(255,255,255,0.05)',
                      border: '1px solid rgba(255,255,255,0.05)'
                    }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <CallIcon sx={{ fontSize: '1rem', color: 'rgba(255,255,255,0.7)' }} />
                          <Typography variant="body2" sx={{ opacity: 0.8, fontWeight: 500 }}>Call Quota</Typography>
                        </Box>
                        <Typography variant="body2" fontWeight="bold" sx={{ color: '#4fc3f7' }}>
                          {customer.callQuotaUsed || 0}/{customer.callQuota || 0} mins
                        </Typography>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={customer.callQuota ? ((customer.callQuotaUsed || 0) / customer.callQuota) * 100 : 0}
                        sx={{ 
                          height: 6, 
                          borderRadius: 3, 
                          bgcolor: 'rgba(255,255,255,0.1)',
                          '& .MuiLinearProgress-bar': {
                            background: 'linear-gradient(90deg, #4fc3f7, #2196f3)',
                            borderRadius: 3
                          }
                        }}
                      />
                    </Box>
                  </Box>

                  {/* Action Buttons */}
                  <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                    {(() => {
                      const planType = customer.subscription_detail?.plan_type?.toLowerCase() || '';
                      const remainingDays = 0; // Since we don't have remaining days in the response
                      const isExpired = false; // Since we don't have expiry status in the response
                      
                      if (planType.includes('free')) {
                        return (
                          <Button
                            variant="contained"
                            startIcon={<UpgradeIcon />}
                            onClick={handleUpgradePlan}
                            fullWidth
                            sx={{ 
                              background: 'linear-gradient(45deg, #ff9800 30%, #ff5722 90%)',
                              color: 'white',
                              fontWeight: 'bold',
                              p: 1.2,
                              borderRadius: 2,
                              textTransform: 'none',
                              fontSize: '0.95rem',
                              boxShadow: '0 4px 12px rgba(255,152,0,0.4)',
                              border: '1px solid rgba(255,255,255,0.2)',
                              '&:hover': {
                                background: 'linear-gradient(45deg, #ff5722 30%, #ff9800 90%)',
                                transform: 'translateY(-2px)',
                                boxShadow: '0 6px 16px rgba(255,152,0,0.5)'
                              },
                              transition: 'all 0.3s ease'
                            }}
                          >
                            {isExpired ? 'Upgrade Now' : 'Upgrade to Premium Plan'}
                          </Button>
                        );
                      } else if (planType.includes('basic')) {
                        return (
                          <Button
                            variant="contained"
                            startIcon={<UpgradeIcon />}
                            onClick={handleUpgradePlan}
                            fullWidth
                            sx={{ 
                              background: 'linear-gradient(45deg, #ff9800 30%, #ff5722 90%)',
                              color: 'white',
                              fontWeight: 'bold',
                              p: 1.2,
                              borderRadius: 2,
                              textTransform: 'none',
                              fontSize: '0.95rem',
                              boxShadow: '0 4px 12px rgba(255,152,0,0.4)',
                              border: '1px solid rgba(255,255,255,0.2)',
                              '&:hover': {
                                background: 'linear-gradient(45deg, #ff5722 30%, #ff9800 90%)',
                                transform: 'translateY(-2px)',
                                boxShadow: '0 6px 16px rgba(255,152,0,0.5)'
                              },
                              transition: 'all 0.3s ease'
                            }}
                          >
                            {isExpired ? 'Upgrade Now' : 'Upgrade to Pro'}
                          </Button>
                        );
                      }
                      return null;
                    })()}
                  </Box>
                </Box>
              </Card>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Customer Snapshot Component */}
      <Box sx={{ position: 'relative', zIndex: 1 }}>
        <CustomerSnapshot customer={customer} />
      </Box>

      {/* Main Content - Two Cards Side by Side */}
      <Box sx={{ 
        display: 'flex', 
        gap: 4, 
        mt: 4,
        flexDirection: { xs: 'column', lg: 'row' },
        pr: { xs: 0, lg: 2 }
      }}>
        {/* Queries Card */}
        <Box sx={{ flex: '0 0 35%', width: '100%' }}>
          <Card sx={{ 
            borderRadius: 3, 
            boxShadow: '0 8px 24px rgba(0,0,0,0.1)', 
            overflow: 'hidden',
            height: '600px',
            display: 'flex',
            flexDirection: 'column',
            border: '1px solid rgba(0,0,0,0.05)',
            position: 'relative'
          }}>
            {/* Background pattern */}
            <Box sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              opacity: 0.02,
              backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='40' height='40' viewBox='0 0 40 40'%3E%3Cg fill-rule='evenodd'%3E%3Cg fill='%23000000' fill-opacity='1'%3E%3Cpath d='M0 38.59l2.83-2.83 1.41 1.41L1.41 40H0v-1.41zM0 1.4l2.83 2.83 1.41-1.41L1.41 0H0v1.41zM38.59 40l-2.83-2.83 1.41-1.41L40 38.59V40h-1.41zM40 1.41l-2.83 2.83-1.41-1.41L38.59 0H40v1.41zM20 18.6l2.83-2.83 1.41 1.41L21.41 20l2.83 2.83-1.41 1.41L20 21.41l-2.83 2.83-1.41-1.41L18.59 20l-2.83-2.83 1.41-1.41L20 18.59z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
              zIndex: 0
            }} />
            
            <Box sx={{ 
              p: 3, 
              borderBottom: '1px solid rgba(0,0,0,0.08)',
              background: 'linear-gradient(135deg, #ff7043 0%, #ff5722 100%)',
              color: 'white',
              flexShrink: 0,
              position: 'relative',
              zIndex: 1,
              boxShadow: '0 4px 12px rgba(255,87,34,0.2)'
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar sx={{ 
                  bgcolor: 'rgba(255,255,255,0.2)', 
                  width: 44,
                  height: 44,
                  boxShadow: '0 4px 8px rgba(0,0,0,0.15)'
                }}>
                  <QuestionAnswerIcon />
                </Avatar>
                <Box>
                  <Typography variant="h6" fontWeight="700" sx={{ 
                    fontSize: '1.25rem',
                    textShadow: '0 1px 2px rgba(0,0,0,0.1)'
                  }}>
                    Customer Queries
                  </Typography>
                  <Typography variant="caption" sx={{ 
                    opacity: 0.9,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5
                  }}>
                    <InfoIcon sx={{ fontSize: '0.9rem' }} />
                    {customer.additionalStats?.pendingQueries || 0} pending queries need attention
                  </Typography>
                </Box>
              </Box>
            </Box>
            
            <Box sx={{ 
              p: { xs: 2.5, md: 3.5 }, 
              flex: 1, 
              overflow: 'auto',
              position: 'relative',
              zIndex: 1,
              bgcolor: 'white',
              '&::-webkit-scrollbar': {
                width: '8px',
              },
              '&::-webkit-scrollbar-track': {
                background: '#f1f1f1',
                borderRadius: '4px',
              },
              '&::-webkit-scrollbar-thumb': {
                background: '#d1d1d1',
                borderRadius: '4px',
                border: '2px solid #f1f1f1',
                '&:hover': {
                  background: '#b1b1b1',
                },
              },
            }}>
              {renderQueriesContent()}
            </Box>
          </Card>
        </Box>

        {/* Invoices Card */}
        <Box sx={{ flex: '0 0 65%' }}>
          <Card sx={{ 
            borderRadius: 3, 
            boxShadow: '0 8px 24px rgba(0,0,0,0.1)', 
            overflow: 'hidden',
            height: '600px',
            display: 'flex',
            flexDirection: 'column',
            border: '1px solid rgba(0,0,0,0.05)',
            position: 'relative'
          }}>
            {/* Background pattern */}
            <Box sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              opacity: 0.02,
              backgroundImage: `url("data:image/svg+xml,%3Csvg width='84' height='48' viewBox='0 0 84 48' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M0 0h12v6H0V0zm28 8h12v6H28V8zm14-8h12v6H42V0zm14 0h12v6H56V0zm0 8h12v6H56V8zM42 8h12v6H42V8zm0 16h12v6H42v-6zm14-8h12v6H56v-6zm14 0h12v6H70v-6zm0-16h12v6H70V0zM28 32h12v6H28v-6zM14 16h12v6H14v-6zM0 24h12v6H0v-6zm0 8h12v6H0v-6zm14 0h12v6H14v-6zm14 8h12v6H28v-6zm-14 0h12v6H14v-6zm28 0h12v6H42v-6zm14-8h12v6H56v-6zm0-8h12v6H56v-6zm14 8h12v6H70v-6zm0 8h12v6H70v-6zM14 24h12v6H14v-6zm14-8h12v6H28v-6zM14 8h12v6H14V8zM0 8h12v6H0V8z' fill='%23000000' fill-opacity='1' fill-rule='evenodd'/%3E%3C/svg%3E")`,
              zIndex: 0
            }} />
            
            <Box sx={{ 
              p: 3, 
              borderBottom: '1px solid rgba(0,0,0,0.08)',
              background: 'linear-gradient(135deg, #3949ab 0%, #283593 100%)',
              color: 'white',
              flexShrink: 0,
              position: 'relative',
              zIndex: 1,
              boxShadow: '0 4px 12px rgba(57,73,171,0.2)'
            }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Avatar sx={{ 
                    bgcolor: 'rgba(255,255,255,0.2)', 
                    width: 44,
                    height: 44,
                    boxShadow: '0 4px 8px rgba(0,0,0,0.15)'
                  }}>
                    <ReceiptIcon />
                  </Avatar>
                  <Box>
                    <Typography variant="h6" fontWeight="700" sx={{ 
                      fontSize: '1.25rem',
                      textShadow: '0 1px 2px rgba(0,0,0,0.1)'
                    }}>
                      Invoice Management
                    </Typography>
                    <Typography variant="caption" sx={{ 
                      opacity: 0.9,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5
                    }}>
                      <InfoIcon sx={{ fontSize: '0.9rem' }} />
                      {customer.additionalStats?.pendingInvoice || 0} pending invoices worth {formatIndianCurrency(customer.additionalStats?.invoiceDue || 0)}
                    </Typography>
                  </Box>
                </Box>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => setCreateInvoiceModal(true)}
                  sx={{ 
                    bgcolor: 'rgba(255,255,255,0.15)', 
                    color: 'white',
                    border: '1px solid rgba(255,255,255,0.3)',
                    borderRadius: 2,
                    px: 2,
                    py: 1,
                    fontWeight: 600,
                    boxShadow: '0 4px 8px rgba(0,0,0,0.15)',
                    '&:hover': {
                      bgcolor: 'rgba(255,255,255,0.25)',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 6px 12px rgba(0,0,0,0.2)'
                    },
                    transition: 'all 0.2s ease'
                  }}
                >
                  Create Invoice
                </Button>
              </Box>
            </Box>
            
            <Box sx={{ 
              flex: 1, 
              overflow: 'auto',
              position: 'relative',
              zIndex: 1,
              bgcolor: 'white',
              '&::-webkit-scrollbar': {
                width: '8px',
              },
              '&::-webkit-scrollbar-track': {
                background: '#f1f1f1',
                borderRadius: '4px',
              },
              '&::-webkit-scrollbar-thumb': {
                background: '#d1d1d1',
                borderRadius: '4px',
                border: '2px solid #f1f1f1',
                '&:hover': {
                  background: '#b1b1b1',
                },
              },
            }}>
              {renderInvoicesContent()}
            </Box>
          </Card>
        </Box>
      </Box>

      {/* Invoice Modal */}
      <CreateInvoice
        open={createInvoiceModal}
        onClose={() => setCreateInvoiceModal(false)}
        onSave={handleSaveInvoice}
        preSelectedCustomer={{
          id: customer.account_info?.id,
          companyName: customer.account_info?.company_name,
          businessInfo: {
            gstin: customer.account_info?.gstin,
            address: customer.account_info?.address
          },
          ownerName: customer.owner_contact?.name,
          ownerEmail: customer.owner_contact?.email
        }}
      />

      {/* Invoice Details Modal */}
      <InvoiceDetails
        open={invoiceDetailsOpen}
        onClose={async () => {
          setInvoiceDetailsOpen(false);
          setOpenPaymentOnMount(false);
          // Refresh customer data to update invoice list
          try {
            const accountDetails = await fetchCustomerAccountDetails(id);
            const invoicesData = await contextFetchAccountInvoices(accountDetails.account_info.id);
            setInvoices(invoicesData.success ? invoicesData.data || [] : []);
          } catch (error) {
            console.error('Error refreshing invoice data:', error);
          }
        }}
        invoice={selectedInvoice}
        openPaymentDialogOnMount={openPaymentOnMount}
        showSuccessMessage={(message) => {
          setSnackbar({
            open: true,
            message: message,
            severity: 'success'
          });
        }}
      />

      {/* Invoice Status Update Dialog */}
      <InvoiceStatusUpdateDialog
        open={statusUpdateDialog.open}
        onClose={handleStatusUpdateClose}
        onConfirm={handleStatusUpdateConfirm}
        invoice={selectedInvoiceForAction}
        action={statusUpdateDialog.action}
        loading={statusUpdateDialog.loading}
      />

      {/* Send Invoice Dialog */}
      <SendInvoiceDialog
        open={sendInvoiceDialog.open}
        onClose={handleSendInvoiceDialogClose}
        onConfirm={handleSendInvoiceDialogConfirm}
        invoice={selectedInvoiceForAction}
        loading={sendInvoiceDialog.loading}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        sx={{ mb: 2, mr: 2 }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          variant="filled"
          sx={{ 
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            borderRadius: 2,
            fontWeight: 500,
            alignItems: 'center'
          }}
          icon={snackbar.severity === 'success' ? <CheckIcon /> : 
                snackbar.severity === 'error' ? <CancelIcon /> : 
                snackbar.severity === 'warning' ? <WarningIcon /> : <InfoIcon />}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CustomerDetails; 